import { useForm } from "@tanstack/react-form";
import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { getGuestList, handlePostRsvp } from "../lib/api";
import type { RSVPResponse } from "../types/rsvp";

export const Route = createFileRoute("/rsvp")({
	component: RouteComponent,
});

function RouteComponent() {
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [submitError, setSubmitError] = useState("");
	const [submitSuccess, setSubmitSuccess] = useState(false);
	const [guestList, setGuestList] = useState<
		{ id: string; name: string; count: number }[]
	>([]);

	const refreshGuestList = useCallback(() => {
		getGuestList().then(({ data }) =>
			setGuestList(data.sort((a, b) => a.name.localeCompare(b.name))),
		);
	}, []);

	useEffect(() => {
		refreshGuestList();
	}, [refreshGuestList]);

	const form = useForm<
		Omit<RSVPResponse, "timestamp">,
		undefined,
		undefined,
		undefined,
		undefined,
		undefined,
		undefined,
		undefined,
		undefined,
		undefined
	>({
		defaultValues: {
			id: "",
			name: "",
			attending: "yes" as "yes" | "no",
			message: "",
			dietaryRestrictions: "",
			accommodation: undefined,
		},
		onSubmit: async ({ value }) => {
			setIsSubmitting(true);
			setSubmitError("");
			setSubmitSuccess(false);

			try {
				await handlePostRsvp({
					...value,
					name:
						guestList.find((guest) => guest.id.toString() === value.id)?.name ||
						"",
					timestamp: new Date().toISOString(),
				});

				form.reset();
				setSubmitSuccess(true);
				refreshGuestList();
			} catch (error) {
				setSubmitError(
					error instanceof Error
						? error.message
						: "Nepodařilo se odeslat odpověď. Zkuste to prosím znovu.",
				);
			} finally {
				setIsSubmitting(false);
			}
		},
	});

	if (submitSuccess) {
		return (
			<div className="container mx-auto px-4 py-16">
				<div className="max-w-2xl mx-auto">
					<div className="mb-8 p-6 bg-green-50 border border-green-200 rounded-lg text-green-700">
						<h2 className="text-2xl font-serif text-green-800 mb-4">
							Děkujeme za vaši odpověď!
						</h2>
						{form.state.values.attending === "yes" ? (
							<p>
								Těšíme se na vaši účast na našem svatebním obřadu. Budeme rádi,
								když s námi strávíte tento významný den.
							</p>
						) : (
							<p>
								Je nám líto, že se nemůžete zúčastnit našeho svatebního obřadu.
								Děkujeme za vaši zprávu a přejeme vám vše nejlepší.
							</p>
						)}
					</div>
					<button
						type="button"
						onClick={() => {
							setSubmitSuccess(false);
							form.reset();
						}}
						className="text-center inline-flex items-center px-4 py-2 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-[#7D9F7D] hover:bg-[#6b9658] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#7D9F7D]"
					>
						Vyplnit další účast
					</button>
				</div>
			</div>
		);
	}

	return (
		<div className="container mx-auto px-4 py-16">
			<div className="max-w-2xl mx-auto">
				<h1 className="text-4xl font-serif text-[#7D9F7D] mb-8 text-center">
					Potvrzení účasti
				</h1>

				{submitError && (
					<div className="mb-8 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
						{submitError}
					</div>
				)}

				<form
					onSubmit={(e) => {
						e.preventDefault();
						e.stopPropagation();
						form.handleSubmit();
					}}
					className="space-y-6"
				>
					{/* Výběr hosta */}
					<div className="bg-white p-6 rounded-lg shadow-md">
						<h2 className="text-2xl font-serif text-[#7D9F7D] mb-4">
							Vyberte své jméno
						</h2>
						<form.Field
							name="id"
							validators={{
								onChange: ({ value }) => {
									if (!value) return "Prosím vyberte své jméno ze seznamu";
									return undefined;
								},
							}}
						>
							{(field) => (
								<div>
									<select
										id="name"
										value={field.state.value}
										onChange={(e) => field.handleChange(e.target.value)}
										className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#7D9F7D] focus:border-transparent"
									>
										<option value="">Vyberte své jméno</option>
										{guestList.map((guest) => (
											<option key={guest.id} value={guest.id}>
												{guest.name}
											</option>
										))}
									</select>
									{field.state.meta.errors ? (
										<p className="mt-1 text-sm text-red-600">
											{field.state.meta.errors.join(", ")}
										</p>
									) : null}
								</div>
							)}
						</form.Field>
					</div>

					{/* Účast */}
					<div className="bg-white p-6 rounded-lg shadow-md">
						<h2 className="text-2xl font-serif text-[#7D9F7D] mb-4">Účast</h2>
						<form.Field name="attending">
							{(field) => (
								<div className="space-y-4">
									<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
										<label className="flex-1">
											<input
												type="radio"
												name="attending"
												value="yes"
												checked={field.state.value === "yes"}
												onChange={() =>
													field.handleChange("yes" as "yes" | "no")
												}
												className="hidden"
											/>
											<div
												className={`p-4 text-center rounded-lg border-2 cursor-pointer transition-all ${
													field.state.value === "yes"
														? "border-[#7D9F7D] bg-[#C1E1C1]/30 text-[#7D9F7D]"
														: "border-gray-200 hover:border-[#C1E1C1]"
												}`}
											>
												<span className="text-lg">Ano, zúčastním se</span>
											</div>
										</label>
										<label className="flex-1">
											<input
												type="radio"
												name="attending"
												value="no"
												checked={field.state.value === "no"}
												onChange={() =>
													field.handleChange("no" as "yes" | "no")
												}
												className="hidden"
											/>
											<div
												className={`p-4 text-center rounded-lg border-2 cursor-pointer transition-all ${
													field.state.value === "no"
														? "border-red-500 bg-red-100/30 text-red-500"
														: "border-gray-200 hover:border-red-200"
												}`}
											>
												<span className="text-lg">
													Omlouvám se, nemohu se zúčastnit
												</span>
											</div>
										</label>
									</div>
								</div>
							)}
						</form.Field>
					</div>

					{/* Stravovací požadavky - zobrazí se pouze při účasti */}
					<form.Subscribe
						selector={(state) => state.values.attending === "yes"}
					>
						{(attending) =>
							attending && (
								<>
									<div className="bg-white p-6 rounded-lg shadow-md">
										<h2 className="text-2xl font-serif text-[#7D9F7D] mb-4">
											Stravovací požadavky
										</h2>
										<form.Field name="dietaryRestrictions">
											{(field) => (
												<div>
													<label
														htmlFor="dietaryRestrictions"
														className="block text-sm font-medium text-gray-700 mb-1"
													>
														Máte nějaké stravovací omezení nebo alergie?
													</label>
													<textarea
														id="dietaryRestrictions"
														className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#7D9F7D] focus:border-transparent h-32"
														placeholder="Napište nám prosím vaše stravovací požadavky nebo alergie..."
														value={field.state.value}
														onChange={(e) => field.handleChange(e.target.value)}
													/>
												</div>
											)}
										</form.Field>
									</div>

									{/* Ubytování */}
									<div className="bg-white p-6 rounded-lg shadow-md">
										<h2 className="text-2xl font-serif text-[#7D9F7D] mb-4">
											Ubytování
										</h2>
										<form.Field name="accommodation">
											{(field) => (
												<div className="space-y-4">
													<div className="grid grid-cols-1 gap-4">
														<label className="flex-1">
															<input
																type="radio"
																name="accommodation"
																value="self"
																checked={field.state.value === "self"}
																onChange={() => field.handleChange("self")}
																className="hidden"
															/>
															<div
																className={`p-4 text-center rounded-lg border-2 cursor-pointer transition-all ${
																	field.state.value === "self"
																		? "border-[#7D9F7D] bg-[#C1E1C1]/30 text-[#7D9F7D]"
																		: "border-gray-200 hover:border-[#C1E1C1]"
																}`}
															>
																<span className="text-lg">Zařídím si sám</span>
															</div>
														</label>
														<label className="flex-1">
															<input
																type="radio"
																name="accommodation"
																value="help"
																checked={field.state.value === "help"}
																onChange={() => field.handleChange("help")}
																className="hidden"
															/>
															<div
																className={`p-4 text-center rounded-lg border-2 cursor-pointer transition-all ${
																	field.state.value === "help"
																		? "border-[#7D9F7D] bg-[#C1E1C1]/30 text-[#7D9F7D]"
																		: "border-gray-200 hover:border-[#C1E1C1]"
																}`}
															>
																<span className="text-lg">
																	Prosím o pomoc s ubytováním
																</span>
															</div>
														</label>
														<label className="flex-1">
															<input
																type="radio"
																name="accommodation"
																value="tent"
																checked={field.state.value === "tent"}
																onChange={() => field.handleChange("tent")}
																className="hidden"
															/>
															<div
																className={`p-4 text-center rounded-lg border-2 cursor-pointer transition-all ${
																	field.state.value === "tent"
																		? "border-[#7D9F7D] bg-[#C1E1C1]/30 text-[#7D9F7D]"
																		: "border-gray-200 hover:border-[#C1E1C1]"
																}`}
															>
																<span className="text-lg">
																	Budu spát ve stanu
																</span>
															</div>
														</label>
													</div>
												</div>
											)}
										</form.Field>
									</div>
								</>
							)
						}
					</form.Subscribe>

					{/* Zpráva */}
					<div className="bg-white p-6 rounded-lg shadow-md">
						<h2 className="text-2xl font-serif text-[#7D9F7D] mb-4">
							Zpráva pro novomanžele
						</h2>
						<form.Field name="message">
							{(field) => (
								<div>
									<label
										htmlFor="message"
										className="block text-sm font-medium text-gray-700 mb-1"
									>
										Vaše zpráva
									</label>
									<textarea
										id="message"
										className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#7D9F7D] focus:border-transparent h-32"
										placeholder="Napište nám zprávu..."
										value={field.state.value}
										onChange={(e) => field.handleChange(e.target.value)}
									/>
								</div>
							)}
						</form.Field>
					</div>

					<div className="text-center">
						<button
							type="submit"
							disabled={isSubmitting}
							className={`bg-[#7D9F7D] text-white px-8 md:px-10 py-4 md:py-5 rounded-full hover:bg-[#6A8A6A] transition-all duration-300 text-lg md:text-xl font-medium shadow-lg hover:shadow-xl transform hover:-translate-y-1 animate-fade-in-delay-3
								isSubmitting ? "opacity-50 cursor-not-allowed" : ""
							}`}
						>
							{isSubmitting ? "Odesílám..." : "Odeslat odpověď"}
						</button>
					</div>
				</form>
			</div>
		</div>
	);
}

export default RouteComponent;
